<template>
	<div class="csv-uploader">
		<input type="file" ref="fileInput" accept=".csv" class="hidden-input" @change="handleFileSelect" />

		<div class="upload-area" @click="triggerFileInput" @drop.prevent="handleFileDrop" @dragover.prevent>
			<div class="upload-content">
				<Icon type="upload-file" class="upload-icon" />
				<span>Click or drag to upload CSV</span>
				<small>Maximum 1000 rows</small>
			</div>
		</div>

		<div v-if="error" class="error-message" ref="errorMessage">
			{{ error }}
		</div>

		<div v-if="preview" class="csv-preview" ref="preview">
			<h4 :class="{'preview-title': true, [type]: true}">{{previewTitle}} <span v-html="type === 'report-settings' ? '- Click the cell to view the tag (click the tag to copy it), double click to edit the cell, hit enter/return to update. Or upload a new CSV to overwrite the current data.' : ''"></span><span v-html="type === 'table' ? '(first 5 rows)' : ''"></span></h4>
			<div class="table-container">
				<table>
					<thead>
						<tr>
							<th v-if="type === 'report-settings'" class="corner-cell"></th>
							<th v-for="(header, index) in preview.headers" :key="header">
								{{ type === 'report-settings' ? String.fromCharCode(65 + index) : header }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(row, index) in preview.rows" :key="index">
							<td v-if="type === 'report-settings'" class="row-number">{{ index + 1 }}</td>
							<td 
								v-for="header in preview.headers" 
								:key="header"
								@dblclick="type !== 'table' && makeEditable($event, row, header, index)"
								@click="showCellReference($event, header, index)"
								:contenteditable="isEditing"
								@keydown.enter.prevent="finishEditing($event)"
								@blur="finishEditing($event)"
								class="cell"
							>{{ row[header] }}
								<div v-if="type === 'report-settings' && activeCellRef === `${header}${index + 1}` && !isEditing" 
									 class="cell-reference-tooltip"
									 @click.stop="copyToClipboard(`${header}${index + 1}`)"
									 :style="tooltipStyle"
								>
									{{ `[[custom-info:${header}${index + 1}]]` }}
									<span class="copy-hint">Click to copy</span>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script>
	import Papa from "papaparse";
	import Icon from "./Display/Icon.vue";

	export default {
		name: "CsvUploader",
		components: { Icon },

		props: {
			// Allowed headers for validation
			allowedHeaders: {
				type: Array,
				default: () => [],
			},
			// Required headers that must be present
			requiredHeaders: {
				type: Array,
				default: () => [],
			},
			// Add new prop for initial data
			initialData: {
				type: Object,
				default: () => null,
			},
			type: {
				type: String,
				default: "table",
			},
		},

		data() {
			return {
				error: null,
				preview: null,
				activeCellRef: null,
				tooltipStyle: null,
				isEditing: false,
			};
		},

		// Add watch for initialData
		watch: {
			initialData: {
				immediate: true,
				handler(newVal) {
					if (!newVal) return;

					if (this.type === 'report-settings') {
						// Convert the flat object structure back to preview format
						const entries = Object.entries(newVal);
						if (entries.length === 0) return;

						// Find the maximum column and row
						const maxCol = Math.max(...entries.map(([ref]) => ref.charCodeAt(0) - 65));
						const maxRow = Math.max(...entries.map(([ref]) => parseInt(ref.slice(1))));

						// Create headers array (A, B, C, etc.)
						const headers = Array.from({ length: maxCol + 1 }, (_, i) => 
							String.fromCharCode(65 + i)
						);

						// Create rows array
						const rows = Array.from({ length: maxRow }, (_, rowIndex) => {
							const rowObj = {};
							headers.forEach(col => {
								const cellRef = `${col}${rowIndex + 1}`;
								rowObj[col] = newVal[cellRef] || '';
							});
							return rowObj;
						});

						this.preview = {
							headers,
							rows
						};
					} else if (newVal.headers && newVal.data) {
						// Handle regular table type
						this.preview = {
							headers: newVal.headers,
							rows: newVal.data.slice(0, this.maxPreviewRows),
						};
					}
				},
			},
		},

		computed: {
			previewTitle() {
				return this.type == "report-settings" ? "Custom Info CSV" : "CSV Preview";
			},
			maxPreviewRows() {
				return this.type === "table" ? 5 : 1000;
			}
		},

		methods: {
			triggerFileInput() {
				this.$refs.fileInput.click();
			},

			handleFileDrop(event) {
				const file = event.dataTransfer.files[0];
				if (file) this.processFile(file);
			},

			handleFileSelect(event) {
				const file = event.target.files[0];
				if (file) this.processFile(file);
			},

			processFile(file) {
				if (!file.type.includes("csv")) {
					this.error = "Please upload a CSV file";
					return;
				}

				Papa.parse(file, {
					header: this.type !== 'report-settings',
					skipEmptyLines: false,
					transformHeader: (header) => {
						if (this.type === 'report-settings') return header;
						return header.trim() === "" || /^_\d+$/.test(header) ? null : header;
					},
					complete: (results) => {
						if (this.type === 'report-settings') {
							results.data = results.data.map(row => {
								return row.reduce((obj, value, index) => {
									obj[String.fromCharCode(65 + index)] = value;
									return obj;
								}, {});
							});
						}

						results.data = results.data.reduce((acc, row, index, array) => {
							const isEmptyRow = this.type === 'report-settings'
								? Object.values(row).every(value => value === null || value.toString().trim() === "")
								: Object.values(row).every(value => value === null || value.toString().trim() === "");

							if (!isEmptyRow) {
								acc.push(row);
								return acc;
							}

							const hasDataAfter = array
								.slice(index + 1)
								.some(futureRow => Object.values(futureRow).some(value => value !== null && value.toString().trim() !== ""));

							if (hasDataAfter) {
								acc.push(row);
							}

							return acc;
						}, []);

						this.validateAndProcessData(results);
					},
					error: (error) => {
						this.error = "Error parsing CSV: " + error.message;
					},
				});
			},

			validateGraphCsvFormat(data) {
				if (!data || !data.length || !data[0]) {
					this.error = `No data?`;
					this.$nextTick(() => this.scrollToElement("errorMessage"));
					return false;
				}

				const headers = Object.keys(data[0]);
				if (headers.length < 2) {
					// Need at least 2 points to make a line
					this.error = `Not enough headers`;
					this.$nextTick(() => this.scrollToElement("errorMessage"));
					return false;
				}

				// Check each row contains only numeric values
				let isOK = data.every((row) => {
					return headers.every((header) => {
						if ( header.substring(0, 7) === "config:" ) return true;
						const value = row[header];
						// Convert to number and check if it's valid
						return !isNaN(Number(value)) || value === "" || value === null || value === undefined;
					});
				});

				if (!isOK) {
					this.error = `Rows must contain numeric values or be empty`;
					this.$nextTick(() => this.scrollToElement("errorMessage"));
				}

				return isOK;
			},

			validateAndProcessData(results) {
				this.error = null;
				this.preview = null;

				if (this.type == "graph" && !this.validateGraphCsvFormat(results.data)) {
					return;
				}

				if (results.data.length > 1000) {
					this.error = "CSV contains more than 1000 rows";
					this.$nextTick(() => this.scrollToElement("errorMessage"));
					return;
				}

				if (this.type === "report-settings") {
					const columnCount = Object.keys(results.data[0]).length;
					const headers = Array.from({ length: columnCount }, (_, i) => String.fromCharCode(65 + i));

					const processedData = results.data.reduce((acc, row, rowIndex) => {
						headers.forEach((colLetter, colIndex) => {
							const value = row[colLetter];
							if (value !== null && value.toString().trim() !== "") {
								const cellReference = `${colLetter}${rowIndex + 1}`;
								acc[cellReference] = value;
							}
						});
						return acc;
					}, {});

					this.preview = {
						headers,
						rows: results.data.slice(0, this.maxPreviewRows)
					};

					this.$emit("upload-success", processedData);
					return;
				}

				const headers = Object.keys(results.data[0]);
				const emptyHeaderColumns = headers
					.map((header, index) => (header.trim() === "" || /^(null|_)\d*$/.test(header) ? index + 1 : null))
					.filter(Boolean);

				if (emptyHeaderColumns.length > 0) {
					const columnCount = emptyHeaderColumns.length;
					this.error = `Headers are empty in ${columnCount} column${columnCount > 1 ? "s" : ""}`;
					this.$nextTick(() => this.scrollToElement("errorMessage"));
					return;
				}

				if (this.allowedHeaders.length) {
					const invalidHeaders = headers.filter((h) => !this.allowedHeaders.includes(h));
					if (invalidHeaders.length > 0) {
						this.error = `Invalid headers found: ${invalidHeaders.join(", ")}`;
						this.$nextTick(() => this.scrollToElement("errorMessage"));
						return;
					}
				}

				const missingRequired = this.requiredHeaders.filter((h) => !headers.includes(h));
				if (missingRequired.length > 0) {
					this.error = `Missing required headers: ${missingRequired.join(", ")}`;
					this.$nextTick(() => this.scrollToElement("errorMessage"));
					return;
				}

				this.preview = {
					headers,
					rows: results.data.slice(0, 5),
				};

				this.$nextTick(() => this.scrollToElement("preview"));

				this.$emit("upload-success", {
					headers,
					data: results.data,
				});
			},

			scrollToElement(refName) {
				if (this.$refs[refName]) {
					this.$refs[refName].scrollIntoView({
						behavior: "smooth",
						block: "nearest",
					});
				}
			},

			makeEditable(event, row, header, index) {
				if (this.type === 'table') return;
				
				this.isEditing = true;
				this.activeCellRef = null;
				event.target.contentEditable = true;
				event.target.focus();
				
				// Clear any text selection
				window.getSelection().removeAllRanges();
				
				// Store original value in case we need to revert
				event.target.dataset.originalValue = row[header];
				
				// Store reference for updating data
				event.target.dataset.header = header;
				event.target.dataset.rowIndex = index;
			},

			finishEditing(event) {
				this.isEditing = false;
				event.target.contentEditable = false;
				
				const newValue = event.target.textContent.trim();
				const header = event.target.dataset.header;
				const rowIndex = parseInt(event.target.dataset.rowIndex);
				
				// Update the preview data
				this.preview.rows[rowIndex][header] = newValue;

				// If it's report-settings type, emit upload-success with updated data
				if (this.type === 'report-settings') {
					// Convert current preview data to the expected format
					const processedData = this.preview.rows.reduce((acc, row, idx) => {
						this.preview.headers.forEach(colLetter => {
							const value = row[colLetter];
							if (value !== null && value.toString().trim() !== "") {
								const cellReference = `${colLetter}${idx + 1}`;
								acc[cellReference] = value;
							}
						});
						return acc;
					}, {});
					
					this.$emit('upload-success', processedData);
				}
			},

			showCellReference(event, header, rowIndex) {
				if (this.type !== 'report-settings' || this.isEditing) return;
				
				const cellRef = `${header}${rowIndex + 1}`;
				this.activeCellRef = this.activeCellRef === cellRef ? null : cellRef;
				
				if (this.activeCellRef) {
					const rect = event.target.getBoundingClientRect();
					this.tooltipStyle = {
						left: `${rect.left + (rect.width / 2)}px`,
						top: `${rect.top - 10}px`
					};
				} else {
					this.tooltipStyle = null;
				}
			},

			copyToClipboard(cellRef) {
				const tag = `[[custom-info:${cellRef}]]`;
				navigator.clipboard.writeText(tag)
					.then(() => {
						this.$snotify.success('Tag copied to clipboard');
						this.activeCellRef = null;
					})
					.catch(() => {
						this.$snotify.error('Failed to copy to clipboard');
					});
			},
		},
	};
</script>

<style lang="less" scoped>
	@import "../assets/css/less/_settings.less";
	.preview-title {
		margin: 1rem 0 0.5rem;
		&.report-settings {
			span {
				color: @red;
			}
		}
	}

	.csv-uploader {
		margin: 1rem 0;

		.hidden-input {
			display: none;
		}

		.upload-area {
			border: 2px dashed #ddd;
			border-radius: 4px;
			padding: 1rem 2rem;
			text-align: center;
			cursor: pointer;
			transition: all 0.3s ease;

			&:hover {
				border-color: #666;
			}

			.upload-content {
				display: flex;
				flex-direction: column;
				align-items: center;
				gap: 0.5rem;

				.upload-icon {
					width: 32px;
					height: 32px;
					opacity: 0.5;
				}

				small {
					color: #666;
				}
			}
		}

		.error-message {
			color: #dc3545;
			margin-top: 0.5rem;
			font-size: 0.9em;
		}

		.csv-preview {
			margin-top: 1rem;

			.table-container {
				width: 100%;
				overflow-x: auto;
				margin-bottom: 0.5rem;
				&::-webkit-scrollbar {
					height: 8px;
				}
				&::-webkit-scrollbar-track {
					background: #f1f1f1;
				}
				&::-webkit-scrollbar-thumb {
					background: #888;
					border-radius: 4px;
				}
			}

			table {
				min-width: 100%;
				border-collapse: collapse;
				font-size: 0.9em;
				position: relative;

				.corner-cell {
					background: #f5f5f5;
					border-bottom: 2px solid #ddd;
					min-width: 40px !important;
				}

				.row-number {
					background: #f5f5f5;
					font-weight: bold;
					position: sticky;
					left: 0;
					min-width: 40px !important;
					text-align: center;
				}

				th,
				td {
					border: 1px solid #ddd;
					padding: 0.5rem;
					text-align: left;
					min-width: 100px;
					max-width: 300px;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
				}

				th {
					background: #f5f5f5;
					position: sticky;
					top: 0;
				}

				.cell {
					cursor: default;
					user-select: none;
					position: relative;
					
					&[contenteditable="true"] {
						cursor: text;
						outline: none;
						user-select: text;
						
						&:focus {
							outline: none;
						}
					}

					.cell-reference-tooltip {
						position: fixed;
						transform: translate(-50%, -100%);
						background: #333;
						color: white;
						padding: 4px 8px;
						border-radius: 4px;
						font-size: 12px;
						white-space: nowrap;
						cursor: pointer;
						z-index: 1000;
						pointer-events: auto;

						&:hover {
							background: #444;
						}

						.copy-hint {
							display: block;
							font-size: 10px;
							opacity: 0.8;
						}

						&::after {
							content: '';
							position: absolute;
							bottom: -5px;
							left: 50%;
							transform: translateX(-50%);
							border-width: 5px 5px 0;
							border-style: solid;
							border-color: #333 transparent transparent;
						}
					}
				}
			}
		}
	}
</style>
